import type { ReactElement } from 'react';
import { useSession } from '../../auth';
import { EmberMascot } from './EmberMascot';
import { CallGlyph } from './CallGlyph';
import { InboundRegistrationCTA } from './InboundRegistrationCTA';
import './JoinEmberCloser.css';

/**
 * Ember as a contact in your phone (2026-09-02 拍板, §4.1): the one inbound-SMS
 * door as a card. On the homepage it closes the first section, after How it
 * works, where the wish to keep Ember around forms (landing_register_open
 * discovery=closer); since 2026-09-24 it also stands on a light's page, after
 * the place's own words and before Nearby (place_register_open
 * discovery=card). The same words and parts on both. Visitors only: a
 * signed-in person already has the contact. The pill is the door (the sheet
 * is the registration); the message/call marks only say what a phone
 * contact does.
 *
 * The card lays itself out by the width of its container (a container query
 * on the nearest inline-size container: the landing artboard, a light's
 * page's own column): one row of three parts where there is room, stacked
 * where there is not.
 */
export function JoinEmberCloser({ returnTo, logName, discovery }: {
  /** Where the browser lands once registered. */
  returnTo: string;
  /** data-log name for the button (registry vocabulary). */
  logName: string;
  /** Which placement of that name this is (data-log-discovery). */
  discovery: string;
}): ReactElement | null {
  const { status } = useSession();
  if (status !== 'anonymous') return null;
  return (
    <section className="join-closer" aria-labelledby={`join-closer-title-${discovery}`}>
      <div className="join-closer-id">
        <span className="join-avatar"><EmberMascot className="join-mascot" /></span>
        <div className="join-id">
          <span className="join-name">Ember</span>
          <span className="join-caption">A contact in your phone</span>
        </div>
        <span className="join-actions" aria-hidden="true">
          <span className="join-action"><MessageGlyph /></span>
          <span className="join-action"><CallGlyph strokeWidth={1.8} /></span>
        </span>
      </div>
      <div className="join-closer-copy">
        <h2 id={`join-closer-title-${discovery}`}>Take Ember with you.</h2>
        <p>Text or call Ember any time, right from your phone. What you tell stays with you, on every device.</p>
      </div>
      <div className="join-foot">
        <InboundRegistrationCTA
          className="join-cta"
          label="Join Ember"
          returnTo={returnTo}
          logName={logName}
          discovery={discovery}
          seen
        />
        <span className="join-note">One text. No password.</span>
      </div>
    </section>
  );
}

function MessageGlyph(): ReactElement {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 12a8 8 0 0 1-8 8H5l-2 2V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8Z" /></svg>;
}
